import { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import parse from 'html-react-parser';
import { useMenuStore } from '../../../zustand/store/useMenuStore';
import useQueryString from '../../../hooks/useQueryString';
import useQueryLinkNavigate from '../../../hooks/useQueryLinkNavigate';
import useConfirm from '../../../hooks/useConfirm';
import useModalStore from '../../../zustand/store/useModalStore';
import { useShallow } from 'zustand/shallow';
import Loading from '../../../component/common/loading/Loading';
import { decodeHtmlEntities } from '../../../utils/htmlEntity.js';

import '../../../css/data-grid.css';
import * as Icons from '../../../images/icons/icons_component';
import * as Theme from '../../../images/icons/icons_theme';

const decodeHtmlEntity = (str) => {
    if (!str) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.documentElement.textContent || '';
};

const NewBoardDetail = ({ boardTypeCode }) => {

    const { returnPage } = useQueryLinkNavigate();
    const { nttSno } = useParams();
    const userId = localStorage.getItem('id');
    const userName = localStorage.getItem('name');
    // 게시판 관리자는 인트라넷과 같이 등급(J001 · J002)만 본다(관리자 표시 · 롤과 상관없다)
    // 관리자는 남의 글 · 댓글까지 손댈 수 있다는 뜻이고, 수정 · 삭제를 할 수 있는지는 관리자도 메뉴 권한을 따른다
    const admin = ['J001', 'J002'].includes(localStorage.getItem('userAuth'));

    // modal open function
    const [showNotiModal, showPromptModal] = useModalStore(useShallow((state) => [state.showNotiModal, state.showPromptModal]));

    // 쿼리스트링 불러오기
    const prevSearch = useLocation().state?.prevSearch;
    const { boardContents, setBoardContents } = useQueryString();

    const permissions = useMenuStore(state => state.permissions);

    // confirm hook
    const confirm = useConfirm();

    const [detailData, setDetailData] = useState([{}]);
    const [isLoading, setIsLoading] = useState(false);
    const [bbsCmms, setBbsCmms] = useState({});

    const getCmtsData = async (detailId, detailNo) => {
        try {
            const paramval = { mstSno: detailId, nttSno: detailNo };

            const response = await axios.get('/commonapi/selectBbsCmts.do',
                { params: paramval }
            );
            setBbsCmms(response.data);
        } catch (e) {
            console.error('board detail comment data 로딩 중 오류 발생', e);
        }
    }

    const getBoardDetailData = async (detailNo, detailPwd, myY) => {
        // 게시글 번호
        const selectNo = detailNo !== undefined ? detailNo : nttSno;

        // 게시글 이동 함수
        const executeDetail = async () => {
            setIsLoading(true);
            try {
                const paramval = { mstSno: boardTypeCode, nttSno: selectNo, userId };

                const response = await axios.get('/commonapi/selectBoardDetail.do',
                    { params: paramval }
                );
                // 댓글 불러오기
                getCmtsData(boardTypeCode, selectNo);

                // 이전글, 다음글 선택시 쿼리스트링 및 게시글 내용 변경
                if (detailNo !== undefined) {
                    returnPage('detail', selectNo, prevSearch);
                }
                setDetailData(response.data);
                setIsLoading(false);
                setBoardContents(response.data.bbsCommon.nttCotClob);
            } catch (e) {
                console.error('board detail data 로딩 중 오류 발생', e);
                // 비밀번호 확인 없이 비밀글 주소로 바로 들어오면 서버가 막는다(403). 안내는 공통 처리가 띄우고 목록으로 돌아간다
                if (e.response?.status === 403) {
                    setIsLoading(false);
                    returnPage('list');
                }
            }
        };

        if (admin || myY || detailNo === undefined || (detailNo !== undefined && detailPwd === 'N')) {
            executeDetail();
            return;
        }

        try {
            let errorMsg = '';
            while (true) {
                const result = await showPromptModal(errorMsg || '비밀글입니다. 비밀번호를 입력하세요.', {
                    placeholder: '비밀번호 입력',
                    type: 'password'
                });
                // 취소 누르면 종료
                if (!result.result) return;

                const params = {
                    nttSno: selectNo,
                    nttPwd: result.value
                };
                // 주소에 남지 않게 본문으로 보낸다. 맞으면 서버 세션이 이 글 열람을 허용한다
                const response = await axios.post('/commonapi/boardPwdCheck.do', params);
                if (response.data) {
                    executeDetail();
                    return;
                };
                errorMsg = '비밀번호가 일치하지 않습니다.\n다시 입력해주세요.';
            }
        } catch (e) {
            console.error('비밀번호 체크 중 오류 발생', e);
        }
    }

    const deleteBoard = async () => {
        try {
            const boardTypeCode2 = boardTypeCode === 11 ? '공지사항' : '자료실';
            const altTypeTxt = '삭제';

            const ok = await confirm(boardTypeCode2 + ' 내용을 ' + altTypeTxt + '하시겠습니까?');
            if (!ok) return false;

            const response = await axios.post('/commonapi/board_delete.do', { nttSno: nttSno }, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response) {
                showNotiModal(altTypeTxt + '가 완료되었습니다.');
                returnPage('list');
            }

        } catch (e) {
            console.error('board delete 중 오류 발생', e);
        }
    }

    useEffect(() => {
        getBoardDetailData();
    }, [nttSno]);

    const bbs = detailData?.bbsCommon || {};
    // 누구 글까지: 남의 글은 관리자만, 업무공유 본인 글은 등록한 날에만(공지사항 · 자료실은 관리자만)
    // 무엇을: 메뉴의 수정(UPYN) · 삭제(DLYN) 권한. 서버도 같은 규칙으로 막는다
    const canManage = admin || (boardTypeCode === 14 && bbs.nttResId === userId && (bbs.regday || '').substring(0, 10) === detailData?.nowdate);
    const bbsprnxt = detailData?.resultPreNext || {};

    const bbsPrY = bbsprnxt.PREV !== 0
    const bbsNxY = bbsprnxt.NEXT !== 0

    const getFileIcon = (fileName) => {
        if (!fileName) return '';
        // 쿼리스트링 제거
        const cleanName = fileName.split('?')[0];
        // 마지막 점 위치
        const lastDot = cleanName.lastIndexOf('.');
        if (lastDot === -1) return '';

        const ext = cleanName.substring(lastDot + 1).toLowerCase();

        if (ext === 'pdf') return <Icons.BoardIcoPdf16 />;
        if (ext === 'hwp') return <Icons.BoardIcoHwp16 />;
        if (ext === 'zip') return <Icons.BoardIcoZip16 />;
        if (ext === 'jpeg' || ext === 'jpg' || ext === 'png' || ext === 'svg') return <Icons.BoardIcoImg16 />;

        // 매칭 안 될 때 기본 아이콘
        return <Icons.BoardIcoFile16 />;
    };

    const fileDown = async (myPost) => {
        if (boardTypeCode !== 14 || (boardTypeCode === 14 && (admin || myPost))) {
            console.log('file down');
        } else {
            showNotiModal('첨부파일은 관리자이거나 등록자인 경우에만 다운로드 가능합니다.');
        }
    };

    const checkPopup = () => {
        const senData = {
            mstSno: boardTypeCode,
            nttSno: nttSno,
        };
        localStorage.setItem('boardCheckData', JSON.stringify(senData));
        let popup = null;
        popup = window.open('/popup/board_check_popup', "팝업", "scrollbars=0, resizable=0, width=1050, height=550, top=30, left=30");
    }

    // 댓글 상태 관리
    const [cmtCot, setCmtCot] = useState('');

    const handleInputChange = (e) => {
        setCmtCot(e.target.value);
    };

    const cmtAdd = async () => {
        try {
            const ok = await confirm('댓글을 등록하시겠습니까?');
            if (!ok) return false;

            if (cmtCot === null || cmtCot === undefined || cmtCot === '') {
                showNotiModal('댓글 내용은 필수입니다.');
                return false;
            };

            const response = await axios.post('/commonapi/comment_register.do', {
                mstSno: boardTypeCode,
                nttSno: nttSno,
                cmtCot: cmtCot
            });

            if (response) {
                showNotiModal(response.data.resultMessage);
                getCmtsData(boardTypeCode, nttSno);
                setCmtCot('');
            }

        } catch (e) {
            console.error('comment 등록 중 오류 발생', e);
        }
    };

    // 수정 중인 댓글 cmt id
    const [editingIds, setEditingIds] = useState(new Set());
    // 댓글 id별 textarea 값 관리
    const [editValues, setEditValues] = useState({});
    // 수정 클릭 시 댓글 id ref
    const textareaRefs = useRef({});
    // 수정 버튼 클릭
    const handleEdit = (id, currentValue) => {
        setEditingIds(prev => {
            const newSet = new Set(prev);
            newSet.add(id);
            return newSet;
        });
        const decodedValue = decodeHtmlEntity(currentValue);
        // 초기값 세팅
        setEditValues(prev => ({
            ...prev,
            [id]: decodedValue
        }));
        // 수정 댓글 포커스
        setTimeout(() => {
            const el = textareaRefs.current[id];
            el?.focus();
            el?.setSelectionRange(el.value.length, el.value.length);
        }, 0);
    };
    // textarea 입력
    const handleChange = (id, value) => {
        setEditValues(prev => ({
            ...prev,
            [id]: value
        }));
    };
    // 수정 완료
    const cmtModify = async (cmtSno) => {
        try {
            const ok = await confirm('댓글을 수정하시겠습니까?');
            if (!ok) return false;

            const newValue = editValues[cmtSno];

            const response = await axios.post('/commonapi/comment_update.do', {
                cmtSno,
                cmtCot: newValue
            });

            if (response) {
                // 해당 댓글만 다시 조회
                const res = await axios.get('/commonapi/selectDetailBbsCmts.do', {
                    params: {
                        mstSno: boardTypeCode,
                        nttSno: nttSno,
                        cmtSno: cmtSno
                    }
                });

                const updatedComment = res.data;

                setBbsCmms(prev => prev.map(item =>
                    item.cmtSno === cmtSno ? updatedComment : item
                ));

                // 수정 상태 해제
                setEditingIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(cmtSno);
                    return newSet;
                });
                showNotiModal('댓글이 정상적으로 수정되었습니다.');
            }

        } catch (e) {
            console.error('comment 수정 중 오류 발생', e);
        }
    };
    // 수정 취소
    const handleCancel = (id) => {
        setEditingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    const cmtDelete = async (cmtSno) => {
        try {
            const ok = await confirm('댓글을 삭제하시겠습니까?');
            if (!ok) return false;

            const response = await axios.post('/commonapi/comment_delete.do', { cmtSno });

            if (response) {
                showNotiModal('삭제가 완료되었습니다.');
                getCmtsData(boardTypeCode, nttSno);
            }

        } catch (e) {
            console.error('comment 삭제 중 오류 발생', e);
        }
    };

    return (
        <>
            {isLoading ? <Loading /> :
                <div className='board_action overflow-scroll pr-1'>
                    <div className='flex flex-col gap-3'>
                        <div className='com-search board border-l border-r' style={{ borderBottomColor: 'var(--co-gray)' }}>
                            <table>
                                <colgroup>
                                    <col width='10%' />
                                    <col width='26%' />
                                    <col width='10%' />
                                    <col width='20%' />
                                    <col width='10%' />
                                    <col width='24%' />
                                </colgroup>
                                <tbody>
                                    <tr>
                                        <th>제목</th>
                                        <td colSpan={5}>
                                            <div className='flex justify-between items-center'>
                                                <strong>{decodeHtmlEntities(bbs.nttTil)}</strong>
                                                {boardTypeCode === 14 &&
                                                    <div className={`co_white px-2 py-1 ml-2 rounded content-center ${bbs.pwdyn !== 'Y' ? 'bg_co_green' : 'bg_co_red'}`}>
                                                        {bbs.pwdyn !== 'Y' ? '전체 공개' : '일부 공개'}
                                                    </div>
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>기관명</th>
                                        <td>
                                            {bbs.emogdesc}
                                        </td>
                                        <th>작성자</th>
                                        <td>
                                            {bbs.nttUsrNick || bbs.nttUsrNam}
                                        </td>
                                        <th>등록일시</th>
                                        <td>
                                            {bbs.regday}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>내용</th>
                                        <td colSpan={5}>
                                            <div className='content_scroll'>
                                                {parse(bbs.nttCotClob || '')}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>첨부파일</th>
                                        <td colSpan='5'>
                                            {boardTypeCode === 14 && (bbs.attFilNam1 || bbs.attFilNam2 || bbs.attFilNam3) && (!admin && bbs.nttResId !== userId) &&
                                                <strong className='co_red'>※ 첨부파일은 관리자이거나 등록자인 경우에만 다운로드 가능합니다.</strong>
                                            }
                                            {!bbs.attFilNam1 ? '' :
                                                <div className='flex gap-2 items-center show_icon'>
                                                    {getFileIcon(bbs.attFilNam1)}
                                                    <button className='ho_color_h cursor-pointer' aria-label='첫번째 첨부파일 다운로드'
                                                            onClick={() => fileDown(bbs.nttResId === userId)}>1.&nbsp; {bbs.attFilNam1}</button>
                                                    <p className='ho_primary'> [{bbs.attFilSze1} KB]</p>
                                                </div>}
                                            {!bbs.attFilNam2 ? '' :
                                                <div className='flex gap-2 items-center show_icon'>
                                                    {getFileIcon(bbs.attFilNam2)}
                                                    <button className='ho_color_h cursor-pointer' aria-label='두번째 첨부파일 다운로드'
                                                            onClick={() => fileDown(bbs.nttResId === userId)}>2.&nbsp; {bbs.attFilNam2}</button>
                                                    <p className='ho_primary'> [{bbs.attFilSze2} KB]</p>
                                                </div>}
                                            {!bbs.attFilNam3 ? '' :
                                                <div className='flex gap-2 items-center show_icon'>
                                                    {getFileIcon(bbs.attFilNam3)}
                                                    <button className='ho_color_h cursor-pointer' aria-label='세번째 첨부파일 다운로드'
                                                            onClick={() => fileDown(bbs.nttResId === userId)}>3.&nbsp; {bbs.attFilNam3}</button>
                                                    <p className='ho_primary'> [{bbs.attFilSze3} KB]</p>
                                                </div>}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className='print_only flex justify-between p-2'>
                            {bbs.nttCmtYon !== 'Y' ? <div></div> :
                                <div className='content-end'>
                                    <p className='ho_color'>댓글 {bbsCmms.length}개</p>
                                </div>
                            }
                            <div className='btn_container flex gap-2 justify-center'>
                                <button onClick={() => returnPage('list')} className='comm_btn bg_co_black'>목록</button>
                                <button onClick={() => window.print()} className='comm_btn_w'>인쇄</button>
                                {/* 답글 · 글쓰기는 관리자도 메뉴 권한(등록·수정 UPYN)을 따른다 */}
                                {bbs.nttRpyYon === 'Y' && permissions?.UPYN === 'Y' &&
                                    <button onClick={() => returnPage('reply', nttSno, prevSearch)} className='comm_btn bg_st_primary'>답글</button>
                                }
                                {canManage &&
                                    <>
                                        {permissions?.UPYN === 'Y' && <button onClick={() => returnPage('edit', nttSno, prevSearch, boardContents)} className='comm_btn bg_ho_primary'>수정</button>}
                                        {permissions?.DLYN === 'Y' && <button onClick={() => deleteBoard()} className='comm_btn bg_tr_primary'>삭제</button>}
                                    </>
                                }
                            </div>
                            <div className='flex gap-2'>
                                <p className='content-center'>조회수 {bbs.nttAcsCnt}</p>
                                <button onClick={checkPopup} className='comm_btn_w board'>조회확인</button>
                            </div>
                        </div>
                        {/* 댓글 */}
                        <div>
                            <div className={`print_cmt bg_ho_odd rounded ${bbs.nttCmtYon === 'Y' && 'mb-2'}`}>
                                {bbsCmms.length > 0 && bbsCmms?.map((item) => {
                                    const myCmt = item.cmtResId === userId;
                                    // 본인 댓글은 글을 볼 수 있으면 고치고 지운다. 남의 댓글은 관리자이면서 메뉴의 수정 · 삭제 권한이 있어야 한다
                                    const canEditCmt = myCmt || (admin && permissions?.UPYN === 'Y');
                                    const canDeleteCmt = myCmt || (admin && permissions?.DLYN === 'Y');
                                    return (
                                        <div key={item.cmtSno}>
                                            <div className='flex flex-col p-4 gap-2'>
                                                <div className='flex justify-between'>
                                                    <span className='bold'>
                                                        {item.cmtUsrNam} [{item.emogdesc}]
                                                        <span className='co_gray ml-3'>{item.regdate}</span>
                                                    </span>
                                                    {(canEditCmt || canDeleteCmt) && !editingIds.has(item.cmtSno) &&
                                                        <div className='flex gap-2'>
                                                            {canEditCmt &&
                                                                <button
                                                                    className='cursor-pointer bold co_dark ho_color_h'
                                                                    onClick={() => handleEdit(item.cmtSno, item.cmtCot)}
                                                                >수정</button>
                                                            }
                                                            {canDeleteCmt &&
                                                                <button
                                                                    className='cursor-pointer bold co_dark tr_bright_color_h'
                                                                    onClick={() => cmtDelete(item.cmtSno)}
                                                                >삭제</button>
                                                            }
                                                        </div>
                                                    }
                                                </div>
                                                {editingIds.has(item.cmtSno) ?
                                                    <div className='flex gap-2'>
                                                        <textarea
                                                            id='cmtCot'
                                                            name='cmtCot'
                                                            ref={(el) => (textareaRefs.current[item.cmtSno] = el)}
                                                            className='border rounded w-full p-2'
                                                            value={editValues[item.cmtSno] ?? ''}
                                                            onChange={(e) => handleChange(item.cmtSno, e.target.value)}
                                                        />
                                                        <button
                                                            className='comm_btn bg_ho_primary h-10 m-auto'
                                                            onClick={() => cmtModify(item.cmtSno)}
                                                            disabled={editValues[item.cmtSno] === ''}
                                                        >수정
                                                        </button>
                                                        <button
                                                            className='comm_btn bg_co_black h-10 m-auto'
                                                            onClick={() => handleCancel(item.cmtSno)}
                                                        >취소
                                                        </button>
                                                    </div>
                                                    :
                                                    <p className='mx-2'>
                                                        {decodeHtmlEntity(item.cmtCot || '')?.split('\n').map((line, idx) => (
                                                            <span key={idx}>
                                                                {line}
                                                                <br />
                                                            </span>
                                                        ))}
                                                    </p>
                                                }
                                            </div>
                                            {<hr className='wp-98 m-auto' />}
                                        </div>
                                    )
                                })}
                                {bbs.nttCmtYon === 'Y' &&
                                    <div className='flex gap-3 p-4'>
                                        <span className='bold whitespace-no-wrap content-center'>{userName}</span>
                                        <textarea
                                            id='cmtCot'
                                            name='cmtCot'
                                            className='border rounded w-full p-2'
                                            value={cmtCot}
                                            onChange={handleInputChange}
                                        />
                                        <button
                                            className='comm_btn bg_co_black h-10 m-auto'
                                            onClick={() => cmtAdd()}
                                            disabled={cmtCot === ''}
                                        >등록
                                        </button>
                                    </div>
                                }
                            </div>
                        </div>
                        <div className='com-search board border-b-2' style={{ borderTopColor: 'var(--co-line)' }}>
                            <table>
                                <colgroup>
                                    <col width='10%' />
                                    <col width='90%' />
                                </colgroup>
                                <tbody>
                                    <tr>
                                        <th>▲ 이전글</th>
                                        <td>
                                            <span role='link' tabIndex={bbsPrY ? 0 : -1}
                                                className={`flex gap-1 items-center ${!bbsPrY ? 'cursor-default' : 'cursor-pointer ho_color_h'}`}
                                                onClick={() => bbsPrY ? getBoardDetailData(bbsprnxt.PREV, bbsprnxt.PREV_NTT_PWD, userId === bbsprnxt.PREV_NTT_RES_ID) : ''}
                                                onKeyDown={(e) => {
                                                    if (bbsPrY && (e.key === 'Enter' || e.key === ' ')) {
                                                        e.preventDefault();
                                                        getBoardDetailData(bbsprnxt.PREV, bbsprnxt.PREV_NTT_PWD, userId === bbsprnxt.PREV_NTT_RES_ID);
                                                    }
                                                }}>
                                                {bbsPrY ? bbsprnxt.PREV_TITLE : '첫번째 게시글 입니다.'}
                                                {bbsPrY && bbsprnxt.PREV_NTT_PWD === 'Y' && <Theme.Pwd16 className='ho_primary'/>}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>▼ 다음글</th>
                                        <td>
                                            <span role='link' tabIndex={bbsNxY ? 0 : -1}
                                                className={`flex gap-1 items-center ${!bbsNxY ? 'cursor-default' : 'cursor-pointer ho_color_h'}`}
                                                onClick={() => bbsNxY ? getBoardDetailData(bbsprnxt.NEXT, bbsprnxt.NEXT_NTT_PWD, userId === bbsprnxt.NEXT_NTT_RES_ID) : ''}
                                                onKeyDown={(e) => {
                                                    if (bbsNxY && (e.key === 'Enter' || e.key === ' ')) {
                                                        e.preventDefault();
                                                        getBoardDetailData(bbsprnxt.NEXT, bbsprnxt.NEXT_NTT_PWD, userId === bbsprnxt.NEXT_NTT_RES_ID);
                                                    }
                                                }}>
                                                {bbsNxY ? bbsprnxt.NEXT_TITLE : '마지막 게시글 입니다.'}
                                                {bbsNxY && bbsprnxt.NEXT_NTT_PWD === 'Y' && <Theme.Pwd16 className='ho_primary'/>}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            }
        </>
    )
}

export default NewBoardDetail;